// --- Share Link Constants ---

/** General access type for share links */
export const SHARE_LINK_ACCESS_TYPES = {
  ANYONE: '0', // anyone with the link
  ACCOUNT: '1', // members within account
  RESTRICTED: '2', // restricted to added emails
} as const;

export type ShareLinkAccessType =
  (typeof SHARE_LINK_ACCESS_TYPES)[keyof typeof SHARE_LINK_ACCESS_TYPES];

/** Access role for share links */
export const SHARE_LINK_ACCESS_ROLES = {
  EDITOR: '2', // can edit & add/upload assets
  VIEWER: '3', // view only
} as const;

export type ShareLinkAccessRole =
  (typeof SHARE_LINK_ACCESS_ROLES)[keyof typeof SHARE_LINK_ACCESS_ROLES];

/** Status codes for share link access (HTTP-like) */
export const SHARE_LINK_ACCESS_STATUS = {
  OK: 200,
  NOT_AUTHORIZED: 401,
  FORBIDDEN: 403,
  NOTFOUND: 404,
  EXPIRED: 419,
  TOO_MANY_ATTEMPTS: 429,
  INVALID_TOKEN: 498,
  ERROR: 5000,
} as const;

export type ShareLinkAccessStatus =
  (typeof SHARE_LINK_ACCESS_STATUS)[keyof typeof SHARE_LINK_ACCESS_STATUS];

/** Error/status messages returned by share link access methods */
export const SHARE_LINK_ACCESS_MESSAGES = {
  PASSWORD_NOT_MATCH: 'PASSWORD_NOT_MATCH',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  NO_ACCESS: 'NO_ACCESS',
  USER_LOGIN_REQUIRED: 'USER_LOGIN_REQUIRED',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
} as const;

export type ShareLinkAccessMessage =
  (typeof SHARE_LINK_ACCESS_MESSAGES)[keyof typeof SHARE_LINK_ACCESS_MESSAGES];

/** Share link object types */
export const SHARE_LINK_TYPES = {
  PLAYLIST: 'playlist',
  FOLDER: 'folder',
} as const;

export type ShareLinkType = (typeof SHARE_LINK_TYPES)[keyof typeof SHARE_LINK_TYPES];

// --- Share Link Interfaces ---

export interface ShareLinkAccess {
  type: ShareLinkAccessType;
  role: ShareLinkAccessRole;
  ts?: string;
}

export interface ShareLinkPassword {
  enabled: boolean;
  value?: string; // AES encrypted — never exposed to client except via reveal
  ts?: string;
}

export interface ShareLinkExpiration {
  enabled: boolean;
  value?: string; // ISO date string
  ts?: string;
}

export interface ShareLinkSecurity {
  userId: string; // email address
  role: ShareLinkAccessRole;
  ts?: string;
}

export interface ShareLink {
  _id: string;
  createdBy?: string;
  createdAt?: string;
  lastUpdatedDate?: string;
  accountId?: string;
  teamId?: string;
  objectId: string;
  type: string;
  subType?: string;
  access: ShareLinkAccess;
  securities?: ShareLinkSecurity[];
  password?: ShareLinkPassword;
  expiration?: ShareLinkExpiration;
}

// --- Access Response Interfaces ---

export interface PreAccessShareLinkResponse {
  statusCode: ShareLinkAccessStatus;
  askPassword?: boolean;
  askEmail?: boolean;
  optionalEmail?: boolean;
  role?: ShareLinkAccessRole;
  teamId?: string;
  message?: string;
}

export interface DoAccessShareLinkResponse {
  statusCode: ShareLinkAccessStatus;
  message?: string;
  type?: string;
  accessType?: ShareLinkAccessType;
  objectId?: string;
  role?: ShareLinkAccessRole;
  teamId?: string;
}

export interface PostAccessShareLinkResponse {
  statusCode: ShareLinkAccessStatus;
  role?: ShareLinkAccessRole;
  teamId?: string;
}
